import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import React from 'react';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import PhoneLinkRingIcon from '@mui/icons-material/PhonelinkRing';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { usePublicZone } from '../pages/zones/zones.hook';
import { CircularProgress, createTheme, Divider, ThemeProvider, Typography, useTheme } from '@mui/material';
import { IZone } from '../models';
import { theme } from '../configs';
import { Theme } from '@mui/material/styles';
import { CleaningHistoryDialog } from '../components/zone-landing-page';
import { EmployeeLoginDialog } from 'components/zone-landing-page/employee-login.dialog';
import { useWindowSize } from 'react-use';
import { SubscribeDialog } from '../components/zone-landing-page/subscribe.dialog';
import { baseUrl } from '../store/apis/apis.const';

const customTheme = (outTheme: Theme, primaryBrandColor: string) =>
  createTheme({
    ...outTheme,
    ...{
      palette: {
        primary: {
          light: primaryBrandColor,
          main: primaryBrandColor,
          // dark: string,
          contrastText: `${primaryBrandColor}30`,
        },
        success: {
          main: '#2AA435',
        },
      },
      components: {
        MuiAlert: {
          styleOverrides: {
            root: {
              borderRadius: 12,
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            contained: {
              boxShadow: 'none',
              textTransform: 'none',
              borderRadius: 100,
              display: 'block',
              width: '100%',
              height: 40,
              color: 'white',
              backgroundColor: primaryBrandColor,
              '&:hover': {
                backgroundColor: `${primaryBrandColor}DD`,
              },
            },
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: {
              paddingLeft: 32,
              paddingRight: 32,
            },
          },
        },
      },
    },
  });

const CleaningHistoryIcon = (props: { color: string }) => {
  return (
    <Box
      style={{
        backgroundColor: props.color,
        width: 60,
        height: 60,
        boxSizing: 'border-box',
        borderRadius: '50%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0 2px 0 0',
        color: 'white',
        position: 'absolute',
        bottom: 33,
      }}
    >
      <HistoryOutlinedIcon fontSize={'large'} />
    </Box>
  );
};
const CleaningHistoryLabel = (props: { color: string }) => {
  return (
    <Box style={{ position: 'relative', bottom: '-18px' }}>
      <Label label="Cleaning History" style={{ color: props.color }} />
    </Box>
  );
};

const Label = (props: { label: string; style?: object }) => {
  return (
    <div style={{ fontSize: 12, ...props.style }}>
      {props.label.split(' ').map((w, i) => {
        return (
          <Box key={i}>
            <span>{w}</span>
            <br />
          </Box>
        );
      })}
    </div>
  );
};

const DisplayError = (props: { error: string }) => {
  return <Typography>{props.error}</Typography>;
};

export const CodeNavigator = (): JSX.Element => {
  const [error, setError] = React.useState<string>();
  const { code } = useParams<{ code: string }>();
  const [cleaningHistoryVisible, setCleaningHistoryVisible] = React.useState(false);
  const [employeeLoginVisible, setEmployeeLoginVisible] = React.useState(false);
  const [subscribeVisible, setSubscribeVisible] = React.useState(false);
  const { width } = useWindowSize();
  const navigate = useNavigate();
  const location = useLocation();

  let selectedZone: IZone | null = null;
  if (code) {
    const result = usePublicZone({ code });
    // eslint-disable-next-line prefer-destructuring
    selectedZone = result[1];
    // todo: need to handle zones that are inactive/disabled
  } else {
    setError('Invalid Zone Code');
  }

  const primaryBrandColor = selectedZone?.building?.organization?.primary_brand_color ?? theme.palette.text.primary;
  const outerTheme = useTheme();

  const styles = {
    container: {
      display: 'flex',
      padding: 2,
    },
  };

  return (
    <Box style={{ backgroundColor: 'white', minHeight: '100vh' }}>
      {!selectedZone ? (
        <CircularProgress size={16} sx={{ marginTop: 0.5 }} />
      ) : error ? (
        <DisplayError error={error} />
      ) : (
        <ThemeProvider theme={customTheme(outerTheme, primaryBrandColor)}>
          <Box sx={{ maxWidth: 600, margin: '0 auto' }}>
            {/* Header */}
            <Box sx={styles.container}>
              <img
                src={`${baseUrl}/organization/${selectedZone.building?.organization_id}/logo`}
                alt={'logo'}
                loading={'lazy'}
                style={{ width: 75, height: 'auto', margin: 12 }}
              />
              <Box style={{ marginLeft: 24, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant={'subtitle2'} style={{ fontWeight: 'bold' }}>
                  {selectedZone.name}
                </Typography>
                <Typography variant={'subtitle2'} style={{ fontWeight: 'bold' }}>
                  Zone #{selectedZone.code}
                </Typography>
                <Typography variant={'subtitle2'}>{selectedZone.building?.address}</Typography>
              </Box>
            </Box>
            <Divider style={{ width, position: 'absolute', left: 0 }} />

            {/* Page content */}
            <Outlet />

            <CleaningHistoryDialog
              zone={selectedZone}
              visible={cleaningHistoryVisible}
              setVisible={setCleaningHistoryVisible}
            />

            <EmployeeLoginDialog
              zone={selectedZone}
              visible={employeeLoginVisible}
              onClose={() => setEmployeeLoginVisible(false)}
            />

            <SubscribeDialog
              zone={selectedZone}
              visible={subscribeVisible}
              onClose={() => setSubscribeVisible(false)}
            />
          </Box>
        </ThemeProvider>
      )}

      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: 100,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
        elevation={3}
      >
        <BottomNavigation showLabels style={{ flexGrow: 1 }}>
          {location.pathname.includes('employee-access') ? (
            <BottomNavigationAction
              label={<Label label="Back" style={{ color: primaryBrandColor }} />}
              icon={<ArrowBackIcon fontSize={'large'} style={{ color: primaryBrandColor }} />}
              onClick={() => navigate(`/code/${selectedZone?.code}`)}
            />
          ) : (
            <BottomNavigationAction
              label={<Label label="Cleaner Login" style={{ color: primaryBrandColor }} />}
              icon={<PersonOutlinedIcon fontSize={'large'} style={{ color: primaryBrandColor }} />}
              onClick={() => setEmployeeLoginVisible(true)}
            />
          )}
          <BottomNavigationAction
            label={<CleaningHistoryLabel color={primaryBrandColor} />}
            icon={<CleaningHistoryIcon color={primaryBrandColor} />}
            onClick={() => setCleaningHistoryVisible(true)}
          />
          <BottomNavigationAction
            label={
              <Label
                label="Service Alerts"
                style={{ position: 'relative', bottom: '-5px', color: primaryBrandColor }}
              />
            }
            icon={<PhoneLinkRingIcon style={{ color: primaryBrandColor }} />}
            onClick={() => setSubscribeVisible(true)}
          />
        </BottomNavigation>
      </Paper>
    </Box>
  );
};
